import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { AlertTriangle, AlertCircle, XCircle, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface SubscriptionDetails {
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  planName: string;
  trialDaysRemaining: number | null;
}

const TrialExpiryBanner: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const checkDismissalStatus = useCallback(() => {
    if (user?.companyId) {
      const dismissedKey = `trialBanner_dismissed_${user.companyId}`;
      const isDismissed = localStorage.getItem(dismissedKey);
      setDismissed(!!isDismissed);
    }
  }, [user?.companyId]);

  useEffect(() => {
    if (user?.companyId) {
      loadSubscription();
      checkDismissalStatus();
    }
  }, [user?.companyId, checkDismissalStatus]);

  // Poll for subscription updates every 30 seconds when banner might be shown
  useEffect(() => {
    if (!user?.companyId) return;

    const interval = setInterval(() => {
      loadSubscription();
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, [user?.companyId]);

  // Refresh when page regains focus (e.g., after returning from Stripe)
  useEffect(() => {
    const handleFocus = () => {
      if (user?.companyId) {
        loadSubscription();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user?.companyId]);

  const loadSubscription = async () => {
    try {
      const response = await apiService.get('/stripe-billing/subscription');
      setSubscription(response.data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    if (user?.companyId) {
      const dismissedKey = `trialBanner_dismissed_${user.companyId}`;
      localStorage.setItem(dismissedKey, 'true');
      setDismissed(true);
    }
  };

  const handleSubscribe = () => {
    navigate('/settings/subscription');
  };

  const handleUpdatePayment = async () => {
    try {
      setActionLoading(true);
      const response = await apiService.post('/stripe-billing/create-portal-session', {
        returnUrl: window.location.href
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      toast.error('Failed to open payment management');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = () => {
    navigate('/settings/subscription');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Determine if banner should be shown
  const shouldShowBanner = () => {
    if (loading || dismissed || !subscription) return false;

    const { status, trialDaysRemaining } = subscription;

    // Show if trialing and 7 days or less remaining
    if (status === 'trialing' && trialDaysRemaining !== null && trialDaysRemaining <= 7) {
      return true;
    }

    // Show if past due or canceled
    if (status === 'past_due' || status === 'canceled') {
      return true;
    }

    return false;
  };

  const getBannerContent = () => {
    if (!subscription) return null;

    const { status, trialDaysRemaining, currentPeriodEnd } = subscription;

    if (status === 'trialing' && trialDaysRemaining !== null && trialDaysRemaining <= 7) {
      return {
        bgColor: 'bg-yellow-500',
        textColor: 'text-yellow-900',
        icon: AlertTriangle,
        message: `Your trial ends in ${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''}. Subscribe now to keep your data and features.`,
        buttonText: 'Subscribe Now',
        buttonAction: handleSubscribe,
        showButton: true
      };
    }

    if (status === 'past_due') {
      return {
        bgColor: 'bg-orange-500',
        textColor: 'text-orange-900',
        icon: AlertCircle,
        message: 'Payment failed. Please update your payment method to avoid service interruption.',
        buttonText: 'Update Payment',
        buttonAction: handleUpdatePayment,
        showButton: true
      };
    }

    if (status === 'canceled') {
      return {
        bgColor: 'bg-red-500',
        textColor: 'text-red-900',
        icon: XCircle,
        message: `Your subscription has been canceled. Access ends on ${currentPeriodEnd ? formatDate(currentPeriodEnd) : 'the current period end'}.`,
        buttonText: 'Reactivate',
        buttonAction: handleReactivate,
        showButton: true
      };
    }

    return null;
  };

  if (!shouldShowBanner()) {
    return null;
  }

  const content = getBannerContent();
  if (!content) return null;

  const Icon = content.icon;

  return (
    <div className={`w-full ${content.bgColor} ${content.textColor} shadow-lg animate-slide-down`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-3">
          <div className="flex items-start flex-1 min-w-0">
            <Icon className="h-5 w-5 mr-2 sm:mr-3 flex-shrink-0 mt-0.5 sm:mt-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm md:text-base font-medium leading-snug sm:leading-normal">
                {content.message}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 w-full sm:w-auto">
            {content.showButton && (
              <button
                onClick={content.buttonAction}
                disabled={actionLoading}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 bg-white ${content.textColor} rounded-md text-xs sm:text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center whitespace-nowrap`}
              >
                {actionLoading ? (
                  <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin mr-1.5 sm:mr-2" />
                ) : null}
                <span className="hidden sm:inline">{content.buttonText}</span>
                <span className="sm:hidden">Subscribe</span>
              </button>
            )}
            
            <button
              onClick={handleDismiss}
              className={`p-1 ${content.textColor} hover:bg-black hover:bg-opacity-10 rounded-md transition-colors flex-shrink-0`}
              aria-label="Dismiss banner"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiryBanner;
